import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["method", "rows", "row", "template", "add", "directHelp", "agentHelp", "total"]
  static values = { defaultFeeAccount: String }

  connect() {
    this.nextRowIndex = Date.now()
    this.refresh()
  }

  methodChanged() { this.refresh() }

  add() {
    const fragment = this.templateTarget.innerHTML.replaceAll("NEW_PROPERTY", String(++this.nextRowIndex))
    this.rowsTarget.insertAdjacentHTML("beforeend", fragment)
    this.refresh()
  }

  remove(event) {
    const row = event.target.closest('[data-rent-collection-rule-target="row"]')
    row.querySelector("[data-destroy]").value = "1"
    row.hidden = true
    this.refresh()
  }

  refresh() {
    const agent = this.methodTarget.value === "agent"
    const rows = this.rowTargets.filter(row => !row.hidden)
    this.addTarget.hidden = !agent
    this.directHelpTarget.hidden = agent
    this.agentHelpTarget.hidden = !agent
    rows.forEach((row, index) => {
      row.querySelector('[name$="[position]"]').value = index * 10
      row.querySelector("[data-remove]").hidden = rows.length === 1
      const fields = row.querySelector("[data-agent-fields]")
      fields.hidden = !agent
      fields.querySelectorAll("input, select").forEach(field => { field.disabled = !agent })
      const fee = row.querySelector("[data-fee-account]")
      if (agent && !fee.value && this.defaultFeeAccountValue) fee.value = this.defaultFeeAccountValue
    })
    this.preview()
  }

  // Decimal inputs are converted to integer pennies; fee multiplication uses
  // BigInt so the displayed preview follows per-property half-up rounding.
  decimal(value) {
    if (!/^\d+(\.\d{1,2})?$/.test(value)) return null
    const [whole, fraction = ""] = value.split(".")
    return BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"))
  }

  money(pennies) {
    const sign = pennies < 0n ? "-" : ""
    const absolute = pennies < 0n ? -pennies : pennies
    return `${sign}£${(absolute / 100n).toLocaleString("en-GB")}.${String(absolute % 100n).padStart(2, "0")}`
  }

  preview() {
    const agent = this.methodTarget.value === "agent"
    this.totalTarget.hidden = !agent
    let total = 0n
    let complete = true
    this.rowTargets.filter(row => !row.hidden).forEach(row => {
      const option = row.querySelector("[data-property]").selectedOptions[0]
      const costCentreName = option?.dataset.prettyName || row.dataset.costCentreName
      const noun = costCentreName.toLowerCase()
      row.querySelector("[data-cost-centre-heading]").textContent = `${costCentreName} allocation`
      row.querySelector("[data-cost-centre-label]").textContent = costCentreName
      const remove = row.querySelector("[data-remove]")
      remove.setAttribute("aria-label", `Remove ${noun}`)
      remove.title = `Remove ${noun}`
      remove.querySelector("[data-remove-label]").textContent = `Remove ${noun}`
      row.querySelector("[data-property] option[value='']").textContent = `Choose a ${noun}`
      const rate = option?.dataset.feePercent || ""
      const percent = this.decimal(rate)
      row.querySelector("[data-fee-description]").textContent = percent === null
        ? `No agent fee percentage is set on this ${noun}.`
        : `Agent fee: ${rate}% including VAT, if charged (from ${noun} settings).`
      const gross = this.decimal(row.querySelector("[data-gross]").value)
      const preview = row.querySelector("[data-preview]")
      if (gross === null || percent === null) {
        preview.textContent = `Enter rent and set the ${noun}'s fee percentage to see the expected net.`
        complete = false
      } else {
        const fee = (gross * percent + 5000n) / 10000n
        total += gross - fee
        preview.textContent = `Agent fee: ${this.money(fee)} · Expected net: ${this.money(gross - fee)}`
      }
    })
    this.totalTarget.textContent = complete ? `Expected net received: ${this.money(total)}` : "Expected net is incomplete."
  }
}
